import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
export function PricingSection() {
  const [sliderValue, setSliderValue] = useState(10000);
  // Calculate price based on slider value
  const calculatePrice = clicks => {
    if (clicks <= 10000) return 10;
    if (clicks <= 20000) return 20;
    if (clicks <= 30000) return 30;
    if (clicks <= 40000) return 40;
    return 50;
  };
  const currentPrice = calculatePrice(sliderValue);
  // Format number with commas
  const formatNumber = num => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  // Get range description based on slider value
  const getClickRange = clicks => {
    if (clicks <= 10000) return 'Up to 10,000 clicks';
    if (clicks <= 20000) return '10,001 - 20,000 clicks';
    if (clicks <= 30000) return '20,001 - 30,000 clicks';
    if (clicks <= 40000) return '30,001 - 40,000 clicks';
    return '40,001 - 50,000 clicks';
  };
  return <section id="pricing" className="py-20 bg-white" data-id="element-547">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-548">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-549">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-550">
            Transparent, Usage-Based Pricing
          </h2>
          <p className="text-xl text-gray-600" data-id="element-551">
            Only pay for what you need with pricing based on the number of
            clicks your links receive within a month.
          </p>
        </div>
        {/* Pricing tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-552">
          {/* Free Plan */}
          <div className="rounded-xl border border-gray-200 shadow p-8 flex flex-col" data-id="element-553">
            <div className="mb-6" data-id="element-554">
              <h3 className="text-2xl font-bold text-[#5e60ce] mb-2" data-id="element-555">
                Free Plan
              </h3>
              <p className="text-gray-600" data-id="element-556">First 1,000 clicks per month</p>
            </div>
            <div className="mb-6" data-id="element-557">
              <span className="text-4xl font-bold text-gray-900" data-id="element-558">$0</span>
              <span className="text-gray-600" data-id="element-559">/mo</span>
            </div>
            <ul className="mb-8 space-y-3 flex-grow" data-id="element-560">
              <li className="flex items-start" data-id="element-561">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-562" />
                <span className="text-gray-700" data-id="element-563">Access to all features</span>
              </li>
              <li className="flex items-start" data-id="element-564">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-565" />
                <span className="text-gray-700" data-id="element-566">Amazon product boxes</span>
              </li>
              <li className="flex items-start" data-id="element-567">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-568" />
                <span className="text-gray-700" data-id="element-569">Affiliate link tracking</span>
              </li>
              <li className="flex items-start" data-id="element-570">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-571" />
                <span className="text-gray-700" data-id="element-572">Custom branding options</span>
              </li>
            </ul>
          </div>
          {/* Pro Plan with Slider */}
          <div className="rounded-xl border border-[#5e60ce] shadow-xl p-8 flex flex-col relative" data-id="element-573">
            <div className="mb-6" data-id="element-574">
              <h3 className="text-2xl font-bold text-[#5e60ce] mb-2" data-id="element-575">
                Pro Plan
              </h3>
              <p className="text-gray-600" data-id="element-576">{getClickRange(sliderValue)}</p>
            </div>
            <div className="mb-6" data-id="element-577">
              <span className="text-4xl font-bold text-gray-900" data-id="element-578">
                ${currentPrice}
              </span>
              <span className="text-gray-600" data-id="element-579">/mo</span>
            </div>
            {/* Slider */}
            <div className="mb-8" data-id="element-580">
              <div className="flex justify-between text-sm text-gray-500 mb-1" data-id="element-581">
                <span data-id="element-582">10k</span>
                <span data-id="element-583">20k</span>
                <span data-id="element-584">30k</span>
                <span data-id="element-585">40k</span>
                <span data-id="element-586">50k</span>
              </div>
              <input type="range" min="10000" max="50000" step="10000" value={sliderValue} onChange={e => setSliderValue(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#5e60ce]" data-id="element-587" />
              <p className="text-sm text-gray-600 mt-2" data-id="element-588">
                {formatNumber(sliderValue)} clicks per month
              </p>
            </div>
            <ul className="mb-8 space-y-3 flex-grow" data-id="element-589">
              <li className="flex items-start" data-id="element-590">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-591" />
                <span className="text-gray-700" data-id="element-592">Everything in Free Plan</span>
              </li>
              <li className="flex items-start" data-id="element-593">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-594" />
                <span className="text-gray-700" data-id="element-595">Priority Support</span>
              </li>
              <li className="flex items-start" data-id="element-596">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-597" />
                <span className="text-gray-700" data-id="element-598">Remove Newsfiliate Branding</span>
              </li>
              <li className="flex items-start" data-id="element-599">
                <CheckIcon className="h-5 w-5 text-[#5e60ce] mr-2 flex-shrink-0 mt-0.5" data-id="element-600" />
                <span className="text-gray-700" data-id="element-601">Advanced Analytics</span>
              </li>
            </ul>
          </div>
          {/* Enterprise Plan */}
          <div className="rounded-xl border border-gray-200 shadow p-8 flex flex-col" data-id="element-602">
            <div className="mb-6" data-id="element-603">
              <h3 className="text-2xl font-bold text-[#5e60ce] mb-2" data-id="element-604">
                Enterprise Plan
              </h3>
              <p className="text-gray-600" data-id="element-605">For high-volume creators</p>
            </div>
            <div className="mb-6" data-id="element-606">
              <span className="text-4xl font-bold text-gray-900" data-id="element-607">Custom</span>
            </div>
            <div className="mb-8 flex-grow" data-id="element-608">
              <p className="text-gray-700" data-id="element-609">
                Our Enterprise Plan is tailored for large creators with over
                50,000 clicks per month. Get a custom solution with dedicated
                support, advanced features, and personalized onboarding.
              </p>
              <p className="mt-4 text-gray-700" data-id="element-610">
                Contact us to discuss your specific needs and we'll create a
                custom package that scales with your growth.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}